const TOKEN_KEY = 'golinks_token';
const POSTMSG_SOURCE = 'tidygolinks-app';

// Broadcast auth changes so the extension's content script (v3.6+) can mirror
// them to chrome.storage. Same-origin only — the message is dispatched to our
// own window and only the content script (which runs on this page) can read it.
function broadcastAuth(op: 'login' | 'logout', token?: string) {
  if (typeof window === 'undefined') return;
  try {
    window.postMessage({ source: POSTMSG_SOURCE, type: 'auth', op, token }, window.location.origin);
  } catch { /* ignore */ }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
  broadcastAuth('login', token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  broadcastAuth('logout');
}

export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* ignore */ }
    const err = new Error(
      (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string')
        ? (body as { error: string }).error
        : `HTTP ${res.status}`,
    ) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (name: string, email: string, password: string) =>
    request<{ token?: string; user?: User; pending?: boolean; message?: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    }),

  resendVerification: (email: string) =>
    request<{ ok: true }>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  forgotPassword: (email: string) =>
    request<{ ok: true }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  magicLink: (email: string) =>
    request<{ ok: true }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email, source: 'web' }),
    }),

  me: () => request<User>('/auth/me'),

  markOnboardingComplete: () =>
    request<{ ok: true }>('/auth/me/onboarding-complete', { method: 'POST' }),

  updateProfile: (data: { name: string }) =>
    request<{ ok: true }>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    request<{ ok: true }>('/auth/me/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteAccount: () =>
    request<{ deleted: true; cascadedWorkspace: boolean }>('/auth/me', { method: 'DELETE' }),

  listLinks: () => request<Link[]>('/links'),

  createLink: (data: { slug: string; url: string; description?: string; alias_of?: number | null }) =>
    request<{ id: number; slug: string; url: string; description: string | null; alias_of: number | null }>(
      '/links',
      { method: 'POST', body: JSON.stringify(data) },
    ),

  updateLink: (slug: string, data: { slug?: string; url: string; description?: string }) =>
    request<{ slug: string; url: string; description: string | null }>(`/links/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteLink: (slug: string) =>
    request<{ deleted: string }>(`/links/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  claimLink: (slug: string) =>
    request<{ claimed: true }>(`/links/${encodeURIComponent(slug)}/claim`, { method: 'POST' }),

  analytics: (period: 7 | 30 | 90 = 30) =>
    request<{
      totalClicks: number;
      dailyClicks: Array<{ date: string; count: number }>;
      topLinks: Array<{ slug: string; url: string; description: string | null; clicks: number }>;
      recentLinks: Array<{ slug: string; url: string; description: string | null; clicked_at: string }>;
    }>(`/analytics?period=${period}`),

  organization: () =>
    request<{
      members: Member[];
      topLinks: Array<{ slug: string; url: string; description: string | null; clicks: number; creatorName: string }>;
      totalLinks: number;
      totalClicks: number;
      minutesSaved: number;
    }>('/organization'),

  updateWorkspace: (data: { name: string }) =>
    request<{ ok: true; name: string }>('/workspace', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  sendSupportMessage: (data: { name: string; email: string; subject: string; message: string }) =>
    request<{ ok: true }>('/support/message', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Super-admin endpoints. All require the caller's email to be in SUPER_ADMINS
  // on the server. UI gating is in useRequireSuperAdmin; calls 403 otherwise.
  superStats: () => request<SuperStats>('/super/stats'),
  superBackupStatus: () => request<BackupStatus>('/super/backup-status'),
  superWorkspaces: () => request<SuperWorkspace[]>('/super/workspaces'),
  superWorkspace: (id: number | string) =>
    request<SuperWorkspaceDetail>(`/super/workspaces/${encodeURIComponent(String(id))}`),
  superDeleteWorkspace: (id: number | string) =>
    request<{ ok: true }>(`/super/workspaces/${encodeURIComponent(String(id))}`, { method: 'DELETE' }),
  superUsers: () => request<SuperUser[]>('/super/users'),
  superUserTimeline: (id: number | string) =>
    request<SuperUserTimeline>(`/super/users/${encodeURIComponent(String(id))}/timeline`),
  superSetUserRole: (id: number | string, role: 'user' | 'admin') =>
    request<{ ok: true }>(`/super/users/${encodeURIComponent(String(id))}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),
  superDeleteUser: (id: number | string) =>
    request<{ ok: true }>(`/super/users/${encodeURIComponent(String(id))}`, { method: 'DELETE' }),
  superLinks: () => request<SuperLink[]>('/super/links'),
  superUpdateLink: (id: number | string, data: { slug?: string; url: string; description?: string }) =>
    request<{ id: number; slug: string; url: string; description: string | null }>(
      `/super/links/${encodeURIComponent(String(id))}`,
      { method: 'PUT', body: JSON.stringify(data) },
    ),
  superEmails: () => request<OnboardingEmailTemplate[]>('/super/emails'),
  superSendTestEmail: (emailKey: string, toEmail: string) =>
    request<{ ok: true; emailKey: string; to: string }>('/super/send-test-email', {
      method: 'POST',
      body: JSON.stringify({ emailKey, toEmail }),
    }),

  // Editable email templates (super admin)
  superEmailTemplates: () => request<EmailTemplate[]>('/super/email-templates'),
  superEmailTemplate: (id: number | string) =>
    request<EmailTemplate>(`/super/email-templates/${encodeURIComponent(String(id))}`),
  superCreateEmailTemplate: (data: EmailTemplateInput) =>
    request<EmailTemplate>('/super/email-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  superUpdateEmailTemplate: (id: number | string, data: Partial<EmailTemplateInput>) =>
    request<EmailTemplate>(`/super/email-templates/${encodeURIComponent(String(id))}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  superDeleteEmailTemplate: (id: number | string) =>
    request<{ ok: true }>(`/super/email-templates/${encodeURIComponent(String(id))}`, { method: 'DELETE' }),
  superPreviewEmailTemplate: (data: { subject: string; body_html: string }) =>
    request<{ subject: string; html: string; context: Record<string, string> }>(
      '/super/email-templates/preview',
      { method: 'POST', body: JSON.stringify(data) },
    ),
  superTestSendEmailTemplate: (id: number | string, toEmail: string) =>
    request<{ ok: true; key: string; to: string }>(
      `/super/email-templates/${encodeURIComponent(String(id))}/test-send`,
      { method: 'POST', body: JSON.stringify({ toEmail }) },
    ),
  superEmailTemplateVariables: () =>
    request<{
      variables: Array<{ key: string; label: string; example: string }>;
      triggerTypes: TriggerType[];
      audienceFilters: AudienceFilter[];
    }>('/super/email-templates-variables'),
};

export interface Workspace {
  domain: string;
  ws_name: string | null;
  is_personal: number;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'user' | 'admin';
  workspace_id: number;
  workspace?: Workspace | null;
  email_verified?: boolean;
  onboarding_complete?: boolean;
  extension_last_seen?: string | null;
  has_google?: boolean;
  has_microsoft?: boolean;
  has_zoho?: boolean;
  has_password?: boolean;
  link_count?: number;
  workspace_other_members?: number;
  is_super_admin?: boolean;
}

export interface Link {
  id: number;
  slug: string;
  url: string;
  description: string | null;
  /** Null when the creator deleted their account — see ownerFor / claim flow. */
  created_by: number | null;
  workspace_id: number;
  created_at: string;
  alias_of: number | null;
  click_count: number;
  creator_email: string | null;
}

export interface Member {
  id: number;
  name: string;
  email: string;
  role: 'user' | 'admin';
  hasExtension: boolean;
  extensionLastSeen: string | null;
  linkCount: number;
  topLink: { slug: string; clicks: number } | null;
}

// ===== Super-admin response shapes =====

export interface SuperStats {
  totalUsers: number;
  totalWorkspaces: number;
  totalLinks: number;
  totalClicks: number;
  signupsThisWeek: number;
  signupsThisMonth: number;
  linksThisWeek: number;
  clicksThisWeek: number;
}

export interface BackupStatus {
  lastBackupAt: string | null;
  lastBackupStatus: string | null;
  r2Configured: boolean;
}

export interface SuperWorkspace {
  id: number;
  domain: string;
  is_personal: number | boolean;
  created_at: string;
  member_count: number;
  link_count: number;
  owner_email: string | null;
  total_clicks: number;
  last_click_at: string | null;
}

export interface SuperWorkspaceMember {
  id: number;
  email: string;
  name: string;
  role: string;
  created_at: string;
  extension_last_seen: string | null;
  link_count: number;
  click_count: number;
}

export interface SuperWorkspaceLink {
  id: number;
  slug: string;
  url: string;
  description: string | null;
  created_by: number | null;
  created_at: string;
  creator_email: string | null;
  click_count: number;
  last_clicked_at: string | null;
}

export interface SuperWorkspaceDetail {
  workspace: {
    id: number;
    domain: string;
    is_personal: number | boolean;
    created_at: string;
  };
  owner_email: string | null;
  users: SuperWorkspaceMember[];
  links: SuperWorkspaceLink[];
  stats: {
    totalClicks: number;
    clicks7d: number;
    clicks30d: number;
    extensionUsers: number;
    totalUsers: number;
  };
}

export interface SuperUser {
  id: number;
  email: string;
  name: string;
  role: string;
  created_at: string;
  extension_last_seen: string | null;
  workspace_domain: string | null;
  workspace_is_personal: number | boolean | null;
  link_count: number;
}

export interface SuperUserTimeline {
  user: {
    id: number;
    email: string;
    name: string;
    created_at: string;
    extension_last_seen: string | null;
  };
  events: Array<{
    event: string;
    metadata: string | null;
    created_at: string;
  }>;
  firstLink: { slug: string; created_at: string } | null;
}

export interface SuperLink {
  id: number;
  slug: string;
  url: string;
  description: string | null;
  workspace_id: number;
  created_by: number | null;
  created_at: string;
  creator_email: string | null;
  workspace_domain: string | null;
  click_count: number;
}

export interface OnboardingEmailTemplate {
  key: string;
  name: string;
  when: string;
  subject: string;
}

export type TriggerType =
  | 'on_signup'
  | 'days_after_signup'
  | 'when_no_extension'
  | 'when_no_links'
  | 'when_link_count'
  | 'manual';

export type AudienceFilter = 'all' | 'personal_only' | 'team_only';

export interface EmailTemplate {
  id: number;
  key: string;
  name: string;
  subject: string;
  body_html: string;
  trigger_type: TriggerType;
  trigger_param: string | null;
  audience_filter: AudienceFilter;
  enabled: boolean;
  send_once: boolean;
  trigger_summary: string;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplateInput {
  key: string;
  name: string;
  subject: string;
  body_html: string;
  trigger_type: TriggerType;
  trigger_param?: string | null;
  audience_filter: AudienceFilter;
  enabled?: boolean;
  send_once?: boolean;
}
